import { useCallback, useMemo } from 'react';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core/react/i18n';

import type { PsfPromoteOrSharePageData } from 'src/app/pages/psf/promoteOrShare/pageContext';
import {
	usePsfPromoteOrSharePageData,
	usePsfPromoteOrSharePageFcm,
} from 'src/app/pages/psf/promoteOrShare/pageContext';

import { useReplacements } from './useReplacements';

const DEFAULT_COPY = {
	headlineText: translationKey('corgi.psf.promote_or_share.headline_without_amount'),
	headlineTextIdentified: translationKey('corgi.psf.promote_or_share.headline_without_amount_identified'),
	progressBarLabelText: translationKey('fe.components.psf.progress_bar_label'),
	shareButtonText: translationKey('fe.components.psf.promote_or_share.share'),
	skipLinkText: translationKey('fe.components.psf.promote_or_share.skip'),
	promoterCountHeading: translationKey('fe.components.psf.promote_or_share.promoter_count_heading'),
	promoterCountText: translationKey('fe.components.psf.promote_or_share.promoter_count_html'),
	subheadingText1: translationKey('corgi.psf.promote_or_share.subheading.signing_step_info_v1'),
	subheadingText2: translationKey('corgi.psf.promote_or_share.subheading.chip_in_request'),
	subheadingText3: translationKey('corgi.psf.promote_or_share.subheading.chip_in_benefit'),
	subheadingContributionHero: translationKey('corgi.psf.promote_or_share.subheading.contribution_hero_v1'),
	subheadingContributionNone: translationKey('corgi.psf.promote_or_share.subheading.contribution_none'),
	impactBoxHeading: translationKey('fe.components.psf.promote_or_share.impact_box_heading'),
	impactBoxMainCopy: translationKey('fe.components.psf.promote_or_share.impact_box_main_copy_html'),
	promoteButtonWithGoalText: translationKey('corgi.psf.promote_or_share.promote_button_with_goal'),
	paymentIconsText: translationKey('corgi.psf.promote_or_share.payment_icons.alt'),
};

export type TranslationName = keyof typeof DEFAULT_COPY;

const DONATION_LANGUAGE_COPY = {
	headlineText: translationKey('fe.components.psf.promote_or_share.headline_with_amount_donation'),
	headlineTextIdentified: translationKey('fe.components.psf.promote_or_share.headline_with_amount_identified_donation'),
	subheadingText1: translationKey('fe.components.psf.promote_or_share.paragraph_1_donation_html'),
	subheadingText2: translationKey('fe.components.psf.promote_or_share.paragraph_2_donation_html'),
	impactBoxMainCopy: translationKey('fe.components.psf.promote_or_share.impact_box_main_copy_donation_html'),
	promoteButtonWithGoalText: translationKey('corgi.psf.promote_or_share.promote_button_with_goal_donation'),
} satisfies Partial<Record<TranslationName, TranslationKey>>;

type Result = ModelHookResult<
	{ keys: Record<TranslationName, TranslationKey> },
	{ translateKey: (name: TranslationName, key: TranslationKey) => string }
>;

function translateSpecificKeys({
	name,
	key,
	translate,
	translatePlural,
	petition,
	replacements,
	useDonationLanguage,
}: {
	name: TranslationName;
	key: TranslationKey;
	translate: (key: TranslationKey, replacements?: Record<string, string>) => string;
	translatePlural: (key: TranslationKey, count: number, replacements?: Record<string, string>) => string;
	petition: PsfPromoteOrSharePageData['petition'];
	replacements: Record<string, string>;
	useDonationLanguage: boolean;
}): string {
	const {
		promotionStats: { promoterCount, promoterCountLocalized, signatureCount },
	} = petition;
	if (name === 'impactBoxMainCopy') {
		return useDonationLanguage ? translatePlural(key, signatureCount, replacements) : '';
	}
	if (name === 'promoterCountText') {
		return translatePlural(key, promoterCount, replacements);
	}
	if (name === 'subheadingContributionHero') {
		return promoterCount > 0 ? translatePlural(key, promoterCount, { contributionCount: promoterCountLocalized }) : '';
	}
	if (name === 'subheadingText3' && useDonationLanguage) {
		return '';
	}
	return translate(key, replacements);
}

export function useCopyI18nKeys(): Result {
	const { useDonationLanguage } = usePsfPromoteOrSharePageFcm();
	const { translate, translatePlural } = useI18n();
	const {
		data: { replacements },
	} = useReplacements();
	const {
		data: { petition },
	} = usePsfPromoteOrSharePageData();

	const translateKey = useCallback(
		(name: TranslationName, key: TranslationKey): string => {
			return translateSpecificKeys({
				name,
				key,
				translate,
				translatePlural,
				petition,
				replacements,
				useDonationLanguage,
			});
		},
		[translate, translatePlural, petition, replacements, useDonationLanguage],
	);

	return {
		data: {
			keys: useMemo(
				() => (useDonationLanguage ? { ...DEFAULT_COPY, ...DONATION_LANGUAGE_COPY } : DEFAULT_COPY),
				[useDonationLanguage],
			),
		},
		actions: {
			translateKey,
		},
	};
}
